import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button } from "@/comn/components"; // 화면 구성 컴포넌트
import { useForm, useFetch, useGrid, useStore, useToast, usePage, useModal } from "@/comn/hooks"; // hook
import { URLS, APIS, SG_RECC_LIST, SF_RECC_SRCH } from "./services/OsciBllReccService"; // 서비스
import dayjs from "dayjs";

/*
 * Reconciliation History List
 * !== 정산 처리 이력 목록 ==!
 */
export const OSCI0406009Q = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003"],
    });
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Reconciliation History Search Form !== 정산 처리 이력 검색 폼 ==!
        reccSrch: useForm({
            defaultSchema: SF_RECC_SRCH,
            defaultValues: {
                strtDt: comnUtils.getDate({ d: -7 }),
                endDt: comnUtils.getDate(),
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Reconciliation History List Grid !== 정산 처리 이력 목록 그리드 ==!
        reccList: useGrid({
            defaultSchema: SG_RECC_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get Reconciliation History List !== 정산 처리 이력 목록 조회 ==!
        getReccList: useFetch({
            api: (page = grid.reccList.page) => {
                return APIS.getReccList(form.reccSrch.getValues(), page, grid.reccList.size);
            },
            enabled: comnUtils.isEmpty(form.reccSrch.errors) && form.reccSrch.isSubmitted,
            key: [grid.reccList.page, grid.reccList.size],
            onSuccess: (data) => {
                setStore(pgeUid, {
                    form: form.reccSrch.getValues(),
                    page: grid.reccList.page,
                    size: grid.reccList.size,
                });
            },
            onError: () => {},
            showToast: true,
        }),
        rqstRecc: useFetch({
            api: () => {
                return APIS.rqstRecc();
            },
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00006", // Processed successfully.
                });
            },
            onError: () => {
                modal.openModal({
                    content: "msg.osc.00015", // An error occurred during processing.
                });
            },
            showToast: true,
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Get Reconciliation History List !== 정산 처리 이력 목록 조회 ==!
        getReccList: () => {
            form.reccSrch.handleSubmit(
                (data) => {
                    grid.reccList.setPage(0);
                    fetch.getReccList.fetch(0);
                },
                (data) => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" });
                },
            )();
        },
        rqstRecc: () => {
            fetch.rqstRecc.fetch();
        },
    };

    /*
     * Declaration Grid Cell Render
     * !== 그리드 셀 렌더 정의 ==!
     */
    const render = {
        grid_reccList: {
            cell: {
                // 요청 시간 클릭 시 정산 처리 이력 상세 화면으로 이동
                rqstDtm: (props: any) => {
                    const { rowValues } = props;
                    return (
                        <span style={{ color: "blue" }}>
                            <Link to={`${URLS.osci0406010q}/${rowValues.rqstId}`}>
                                {rowValues.rqstDtm ? dayjs(rowValues.rqstDtm).format("DD/MM/YYYY HH:mm:ss") : ""}
                            </Link>
                        </span>
                    );
                },
            },
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.getReccList();
    }, []);

    return (
        <Page>
            <form>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>{<Group.Control {...form.reccSrch.schema.sendDt}></Group.Control>}</Group.Row>
                        </Group.Section>
                        <Layout direction="row">
                            <Layout.Left>
                                <Button
                                    role="reset"
                                    onClick={() => {
                                        form.reccSrch.reset();
                                    }}
                                ></Button>
                            </Layout.Left>
                            <Layout.Right>
                                <Button
                                    role="search"
                                    onClick={() => {
                                        handler.getReccList();
                                    }}
                                ></Button>
                            </Layout.Right>
                        </Layout>
                    </Group.Body>
                </Group>
            </form>

            <Group>
                <Group.Body>
                    <Layout direction="row">
                        <Layout.Right>
                            <Button
                                onClick={() => {
                                    handler.rqstRecc();
                                }}
                            >
                                {t("B_RECC_RQST")}
                            </Button>
                        </Layout.Right>
                    </Layout>
                    <Group.Section>
                        <Grid
                            {...grid.reccList.grid}
                            data={fetch.getReccList.data?.reccList}
                            render={render.grid_reccList}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
        </Page>
    );
};
